import { AppI, ListingType, DOWNTOWN, CityI, GeolocationI } from "~/interfaces";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";

export type Actions = {
  // city
  removeCity: () => void;
  updateCity: (city: any | undefined) => void;

  // modal
  removeModal: () => void;
  updateModal: (modal: string) => void;

  // pathname
  removePathname: () => void;
  updatePathname: (pathname: string) => void;

  // scrollY
  removeScrollY: () => void;
  updateScrollY: (scrollY: number) => void;

  // calendar
  removeCalendar: () => void;
  updateCalendar: (calendar: any) => void;

  // query
  removeQuery: () => void;
  updateQuery: (query: string) => void;

  // geolocation
  removeGeolocation: () => void;
  updateGeolocation: (geolocation: any) => void;

  // refinementList
  removeRefinementList: () => void;
  updateRefinementList: (refinementList: any) => void;

  // configure
  removeConfigure: () => void;
  updateConfigure: (configure: any) => void;

  // menu
  toggleCalendar: () => void;
  toggleMainMenu: () => void;
  toggleLikeMenu: () => void;
  toggleInterestMenu: () => void;
  toggleFilterMenu: () => void;
  toggleSearch: () => void;
  removeMenu: () => void;
  updateMenu: (menu: any) => void;

  // reset
  resetApp: () => void;
};

export type State = {
  app: AppI;
};

const initialApp: AppI = {
  city: undefined,
  modal: "",
  pathname: "",
  scrollY: 0,
  calendar: {
    dates: [new Date()],
  },
  query: "",
  geolocation: {
    geoCenter: DOWNTOWN,
    radius: 20,
    latitude: "",
    longitude: "",
  },
  refinementList: {
    type: [
      ListingType.ACTIVITY,
      ListingType.EVENT,
      ListingType.BAR,
      ListingType.RESTAURANT,
    ],
    interest_tags: [],
    price_tags: [],
    crowd_tags: [],
  },
  configure: { filters: "" },
  menu: {
    isCalendarActive: false,
    isMainMenuActive: false,
    isFilterMenuActive: false,
    isLikeMenuActive: false,
    isSearchActive: false,
    isBackButtonActive: false,
    isInterestMenuActive: false,
  },
};

export const useAppStore = create<State & Actions>()(
  persist(
    immer((set) => ({
      app: initialApp,

      // city
      removeCity: () =>
        set((state) => {
          state.app.city = undefined;
        }),
      updateCity: (city) =>
        set((state) => {
          state.app.city = city;
        }),

      // modal
      removeModal: () =>
        set((state) => {
          state.app.modal = "";
        }),
      updateModal: (modal) =>
        set((state) => {
          state.app.modal = modal;
        }),

      // pathname
      removePathname: () =>
        set((state) => {
          state.app.pathname = "";
        }),
      updatePathname: (pathname) =>
        set((state) => {
          state.app.pathname = pathname;
        }),

      // scrollY
      removeScrollY: () =>
        set((state) => {
          state.app.scrollY = 0;
        }),
      updateScrollY: (scrollY) =>
        set((state) => {
          state.app.scrollY = scrollY;
        }),

      // calendar
      removeCalendar: () =>
        set((state) => {
          state.app.calendar = { ...initialApp.calendar };
        }),
      toggleCalendar: () =>
        set((state) => {
          state.app.menu.isCalendarActive = !state.app.menu.isCalendarActive;
        }),

      updateCalendar: (calendar) =>
        set((state) => {
          state.app.calendar = { ...state.app.calendar, ...calendar };
        }),

      // query
      removeQuery: () =>
        set((state) => {
          state.app.query = "";
        }),
      updateQuery: (query) =>
        set((state) => {
          state.app.query = query;
        }),

      // geolocation
      removeGeolocation: () =>
        set((state) => {
          state.app.geolocation = initialApp.geolocation;
        }),
      updateGeolocation: (geolocation) =>
        set((state) => {
          state.app.geolocation = { ...state.app.geolocation, ...geolocation };
        }),

      // refinementList
      removeRefinementList: () =>
        set((state) => {
          state.app.refinementList = {
            type: [],
            interest_tags: [],
            price_tags: [],
            crowd_tags: [],
          };
        }),

      updateRefinementList: (refinementList) =>
        set((state) => {
          state.app.refinementList = {
            ...state.app.refinementList,
            ...refinementList,
          };
        }),

      // configure
      removeConfigure: () =>
        set((state) => {
          state.app.configure = { ...initialApp.configure };
        }),

      updateConfigure: (configure) =>
        set((state) => {
          state.app.configure = { ...state.app.configure, ...configure };
        }),
      // menu
      toggleMainMenu: () =>
        set((state) => {
          state.app.menu.isMainMenuActive = !state.app.menu.isMainMenuActive;
        }),
      toggleLikeMenu: () =>
        set((state) => {
          state.app.menu.isLikeMenuActive = !state.app.menu.isLikeMenuActive;
        }),
      toggleSearch: () =>
        set((state) => {
          state.app.menu.isSearchActive = !state.app.menu.isSearchActive;
        }),
      toggleFilterMenu: () =>
        set((state) => {
          state.app.menu.isFilterMenuActive =
            !state.app.menu.isFilterMenuActive;
        }),
      toggleInterestMenu: () =>
        set((state) => {
          state.app.menu.isInterestMenuActive =
            !state.app.menu.isInterestMenuActive;
        }),

      removeMenu: () =>
        set((state) => {
          state.app.menu = { ...initialApp.menu };
        }),

      updateMenu: (menu) =>
        set((state) => {
          state.app.menu = { ...state.app.menu, ...menu };
        }),

      // Reset all app info to initial state
      resetApp: () =>
        set((state) => {
          state.app = { ...initialApp };
        }),
    })),
    {
      name: "app-store",
      storage: createJSONStorage(() => sessionStorage),
      skipHydration: true,
      partialize: (state) => ({
        app: {
          calendar: state.app.calendar,
          city: state.app.city,
          configure: state.app.configure,
          geolocation: state.app.geolocation,
          pathname: state.app.pathname,
          query: state.app.query,
          refinementList: state.app.refinementList,
          scrollY: state.app.scrollY,
        },
      }),
    },
  ),
);
